<template>
    <div>
        <v-dialog v-model="importDialog" persistent width="600px" fullscreen hide-overlay
            transition="dialog-bottom-transition">
            <v-toolbar dark color="primary">
                <v-toolbar-title>Import de vos prestataires</v-toolbar-title>

                <div class="text-right w-100">
                    <v-btn icon dark @click="closeDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </v-toolbar>

            <v-stepper v-model="currentStep">
                <v-stepper-header>
                    <v-stepper-step step="1">Sélection fichier</v-stepper-step>

                    <v-divider></v-divider>

                    <v-stepper-step step="2">Vérifications</v-stepper-step>

                    <v-divider></v-divider>

                    <v-stepper-step step="3">Tags</v-stepper-step>
                </v-stepper-header>

                <v-stepper-items>
                    <v-stepper-content step="1">
                        <v-alert text class="mb-5">
                            <p>
                                La liste des prestataires à contacter pendant vos campagnes se définit au moyen d'un
                                export Excel personnalisé généré depuis Apidae.<br />
                                Il convient d'employer le <b>modèle d'export réseau</b> suivant :
                                <code>Fournisseur de services - Dahub</code>.
                            </p>

                            <v-expansion-panels>
                                <v-expansion-panel>
                                    <v-expansion-panel-header>Quel e-mail sera utilisé pour contacter mes prestataires ?
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <p>
                                            Par défaut, Dahub piochera le premier e-mail présent dans la liste des moyens
                                            de communication de la fiche Apidae pour l'utiliser comme destinataire des
                                            demandes d'information.<br />
                                            Dans le cas où vous souhaitez contacter un autre e-mail, il est possible
                                            d'ajouter une colonne <b>destinataire</b> dans le fichier d'export et d'y
                                            inscrire l'e-mail à utiliser.
                                        </p>

                                        <p>
                                            Par exemple :
                                        </p>
                                        <p>
                                            <v-img src="images/ex_tableur.png" contain height="100px" max-width="870"
                                                position="left"></v-img>
                                        </p>
                                        <p>
                                            <i>Le Moulin de Charrier</i> sera contacté sur
                                            <i>Elie.Coptere@moulin.fr</i>.<br />
                                            Les autres prestataires seront contacté sur le premier e-mail présent dans
                                            leur liste de moyens de communication.
                                        </p>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>

                                <v-expansion-panel>
                                    <v-expansion-panel-header>Puis-je importer plusieurs fois des prestataires ?
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <p>
                                            Vous pouvez importer des prestataires autant de fois que nécessaire.<br />
                                            Si vous importez un prestataire déjà existant sur Dahub, nous mettrons
                                            automatiquement ses données à jour (notamment le destinataire).
                                        </p>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>

                                <v-expansion-panel>
                                    <v-expansion-panel-header>Dahub prend-il en charge tous les types de fiches ?
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <p>
                                            Non, certains types de fiche ne sont pas compatibles avec Dahub :
                                        <ul>
                                            <li>Entité Juridique</li>
                                            <li>Séjour et produit packagé</li>
                                            <li>Domaine skiable / domaine VTT</li>
                                        </ul>
                                        Ces fiches ne seront pas importées.
                                        </p>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>

                        </v-alert>

                        <v-alert type="info" text class="mb-5">
                            Bien s'assurer que la colonne <b>Identifiant Apidae</b> est présente dans l'export, dans le
                            cas contraire nous ne pourrons pas importer les prestataires dans Dahub.
                        </v-alert>


                        <div class="d-flex justify-content-between">
                            <div class="d-flex w-100">
                                <div class="w-100">
                                    <v-file-input @change="loadFile"
                                                  v-model="file"
                                                  accept="application/vnd.ms-excel"
                                                  label="Liste des prestataires (.xls)"
                                                  outlined
                                                  style="padding-top: 0 !important; min-width: 200px;"
                                                  :rules="rules"
                                                  dense>
                                    </v-file-input>

                                    <v-alert v-if="error" type="error">
                                        {{ error }}
                                    </v-alert>
                                </div>

                                <v-btn class="default-button ml-3 load-button" :disabled="file === null || file === undefined || !validFile"
                                       @click="loadProviders">
                                    Charger <v-icon>keyboard_arrow_right</v-icon>
                                </v-btn>
                            </div>

                            <span class="d-flex justify-center font-weight-black"
                                  v-if="account.tis_type.code === 'APIDAE'"
                                  style="margin: 7px 60px 0 60px">OU</span>

                            <div class="d-flex w-100" v-if="account.tis_type.code === 'APIDAE'">
                                <v-text-field v-model="apidaeId"
                                              label="Identifiant Apidae du prestataire"
                                              outlined
                                              dense
                                              style="padding-top: 0 !important; min-width: 200px;"
                                              prepend-icon="tag">
                                </v-text-field>

                                <div class="actions d-flex justify-content-end">
                                    <v-btn class="default-button ml-3 load-button"
                                           :disabled="apidaeId === null || apidaeId === ''"
                                           @click="importProviderById">
                                        Importer le prestataire  <v-icon>keyboard_arrow_right</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        </div>
                    </v-stepper-content>
                </v-stepper-items>

                <v-stepper-content step="2" class="h-100">
                    <div class="container w-50" v-if="showProgress">
                        <span>Nous vérifions l'existence de vos fiches sur Apidae.</span>
                        <v-progress-linear v-model="progress" height="20" class="mb-5" rounded stream striped reactive>
                            <strong>{{ Math.ceil(progress) }}%</strong>
                        </v-progress-linear>
                    </div>

                    <v-expansion-panels v-model="panel" v-if="!showProgress" mandatory>

                        <v-expansion-panel v-if="providersErrors.length">
                            <v-expansion-panel-header>
                                <div class="table-title">
                                    <v-icon color="warning">warning</v-icon> Fiches en erreur ({{ providersErrors.length
                                    }})
                                </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-simple-table height="calc(100vh - 400px)" fixed-header>
                                    <thead>
                                        <tr>
                                            <th class="text-left">Apidae id</th>
                                            <th class="text-left">Nom</th>
                                            <th class="text-left">Catégorie</th>
                                            <th class="text-left">Destinataire</th>
                                            <th class="text-left">Téléphone</th>
                                            <th class="text-left">Référent</th>
                                            <th class="text-left">Erreur</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="provider in providersErrors" :key="provider.id">
                                            <td>
                                                <a target="_blank"
                                                    :href="'https://base.apidae-tourisme.com/gerer/objet-touristique/' + provider.id + '/consulter/'">
                                                    {{ provider.id }}
                                                </a>
                                            </td>
                                            <td>
                                                <span v-if="provider.nom !== undefined">{{
                                                provider.nom.libelleFr}}</span>
                                            </td>
                                            <td>{{ provider.type }}</td>
                                            <td>{{ provider.email }}</td>
                                            <td>{{ provider.phone }}</td>
                                            <td>{{ provider.referent }}</td>
                                            <td>{{ provider.error.label }}</td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                            </v-expansion-panel-content>
                        </v-expansion-panel>

                        <v-expansion-panel v-if="providersToImport.length">
                            <v-expansion-panel-header>
                                <div class="table-title">
                                    <v-icon color="success">check</v-icon> Fiches valides ({{ providersToImport.length
                                    }})
                                </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-simple-table height="calc(100vh - 400px)" fixed-header>
                                    <thead>
                                        <tr>
                                            <th class="text-left">Apidae id</th>
                                            <th class="text-left">Nom</th>
                                            <th class="text-left">Catégorie</th>
                                            <th class="text-left">Destinataire</th>
                                            <th class="text-left">Téléphone</th>
                                            <th class="text-left">Référent</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="provider in providersToImport" :key="provider.id">
                                            <td>
                                                <a target="_blank"
                                                    :href="'https://base.apidae-tourisme.com/gerer/objet-touristique/' + provider.id + '/consulter/'">
                                                    {{ provider.id }}
                                                </a>
                                            </td>
                                            <td>
                                                <span v-if="provider.nom !== undefined">{{
                                                provider.nom.libelleFr}}</span>
                                            </td>
                                            <td>{{ provider.category.name }}</td>
                                            <td>{{ provider.email }}</td>
                                            <td>{{ provider.phone }}</td>
                                            <td>{{ provider.referent }}</td>
                                            <td>
                                                <v-tooltip bottom>
                                                    <template v-slot:activator="{ on }">
                                                        <v-btn v-on="on" icon color="primary"
                                                            @click="prepareUpdateContact(provider)">
                                                            <v-icon>edit</v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Modifier le destinataire</span>
                                                </v-tooltip>
                                            </td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>

                    <div class="actions d-flex justify-content-between mt-5 position-absolute"
                        style="bottom: 20px; width: calc(100% - 58px);" v-if="!showProgress">

                        <v-btn class="default-button" @click="currentStep = 1">
                            <v-icon>keyboard_arrow_left</v-icon> Sélectionner un fichier
                        </v-btn>
                        <v-btn class="default-button ml-3 load-button" @click="currentStep = 3">
                            Organiser mes {{ providersToImport.length }} fiches <v-icon>keyboard_arrow_right</v-icon>
                        </v-btn>
                    </div>
                </v-stepper-content>

                <v-stepper-content step="3">
                    <v-alert text>
                        <p>
                            Afin de vous faciliter l'organisation ou le tri de vos fiches dans Dahub, il est possible dès
                            maintenant de leur associer un <code>tag</code>.
                        </p>
                        <p>
                            Par exemple : vous pourrez plus tard décider d'action spécifiquement dédiées aux fiches
                            taggées <code>adhérents 2020</code>.
                        </p>
                    </v-alert>

                    <v-alert type="info" text>
                        Cette action est <b>optionnelle</b>, vous pourrez taguer vos fiches ultérieurement dans Dahub.
                    </v-alert>

                    <v-combobox outlined class="w-50" label="Tags" multiple chips small-chips :items="tags"
                        item-text="name" deletable-chips v-model="selectedTags" return-object>
                    </v-combobox>

                    <div class="actions d-flex justify-content-between mt-5" v-if="!showProgress">
                        <v-btn class="default-button" @click="currentStep = 2">
                            <v-icon>keyboard_arrow_left</v-icon> Vérifier mes fiches
                        </v-btn>
                        <v-btn class="default-button ml-3 load-button" @click="importProviders">
                            Importer
                            <span class="ml-1 mr-1" v-if="selectedTags.length">et taguer</span>
                            {{ providersToImport.length }} fiches
                        </v-btn>
                    </div>
                </v-stepper-content>
            </v-stepper>
        </v-dialog>

        <update-receiver-modale :edit-contact-modal="editContactModal" :selected-contact="selectedContact"
            :selected-provider="selectedProvider" :no-db="true">
        </update-receiver-modale>
    </div>
</template>

<script>
import { mapState } from "vuex"
import UpdateReceiverModale from "@/components/provider/UpdateReceiverModale"

export default {
    name: "ImportApidae",
    components: { UpdateReceiverModale },
    props: ['importDialog'],
    data() {
        return {
            currentStep: 1,
            file: null,
            providersToCheck: [],
            providersErrors: [],
            checkedProviders: [],
            notFoundProviders: [],
            unhandledProviders: [],
            providersToImport: [],
            progress: 0,
            showProgress: false,
            selectedTags: [],
            panel: [0],
            error: null,
            rules: [
                value => !value || value.name.substr(value.name.length - 3) === 'xls' || 'Le fichier doit être au format .xls',
            ],
            validFile: false,
            editContactModal: false,
            selectedContact: null,
            selectedProvider: [],
            apidaeId: null,

        }
    },
    methods: {
        loadFile(file) {
            if (file !== undefined) {
                this.file = file
                this.validFile = file.name.substr(file.name.length - 3) === 'xls'
            }
        },
        loadProviders() {
            let formData = new FormData
            formData.append('import', this.file)
            formData.append('fieldId', 'identifiantApidae')

            this.$global.loading = true
            this.providersErrors = []
            this.error = null

            this.$http.post('auth/providers/import-providers', formData).then(res => {
                this.$global.loading = false
                this.providersToCheck = res.body.data.providers
                this.error = res.body.data.error

                if (this.error === null) {
                    this.checkApidaeProviders()
                    this.currentStep = 2
                }
            })
        },
        async checkApidaeProviders() {
            let providers = collect(this.providersToCheck)
            let chunk = providers.chunk(20).all()
            let total = chunk.length
            let iterations = 0

            this.checkedProviders = collect()
            this.notFoundProviders = []
            this.unhandledProviders = []
            this.progress = 0
            this.showProgress = true

            const start = async () => {
                await this.asyncForEach(chunk, async (prov) => {
                    let payload = {
                        providers: prov
                    }

                    let res = await this.$http.post('auth/providers/check-apidae-providers', payload)

                    let notFound = res.body.data.providersNotFound
                    let unhandled = res.body.data.unhandledProviders
                    let providersRes = res.body.data.providersFound

                    providersRes.forEach(provider => {
                        this.checkedProviders.push(provider)
                    })

                    notFound.forEach(provider => {
                        provider.error = {
                            code: 1,
                            label: 'Fiche inexistante'
                        }

                        this.notFoundProviders.push(provider)
                        this.providersErrors.push(provider)
                    })

                    unhandled.forEach(provider => {
                        provider.error = {
                            code: 1,
                            label: 'Typologie incompatible'
                        }

                        this.unhandledProviders.push(provider)
                        this.providersErrors.push(provider)
                    })

                    iterations++

                    this.progress = (iterations / total) * 100
                })

                this.providersToImport = this.checkedProviders.all()

                setTimeout(() => {
                    this.showProgress = false
                }, 500)
            }

            start()
        },
        async asyncForEach(array, callback) {
            for (let index = 0; index < array.length; index++) {
                await callback(array[index], index, array)
            }
        },
        importProviders() {
            setTimeout(() => {
                let newTags = []
                let tags = []

                this.selectedTags.forEach(tag => {
                    if (typeof tag === "object") {
                        tags.push(tag)
                    } else {
                        newTags.push(tag)
                    }
                })

                let payload = {
                    providers: this.providersToImport,
                    newTags: newTags,
                    tags: tags,
                    tisType: 'APIDAE'
                }

                this.$global.loading = true

                this.$http.post('auth/providers/import', payload).then(res => {
                    this.selectedTags = []

                    this.$global.loading = false
                    this.$global.$emit('load-providers')

                    this.$store.dispatch('account/fetchCommunes')
                    this.$store.dispatch('account/fetchCategories')

                    this.currentStep = 1
                })
            }, 500)
        },
        closeDialog() {
            this.$global.$emit('close-import')
            this.currentStep = 1
        },
        prepareUpdateContact(provider) {
            this.editContactModal = true
            this.selectedContact = provider.email
            this.selectedProvider = provider
        },
      importProviderById() {
        this.checkedProviders = collect()
        this.notFoundProviders = []
        this.unhandledProviders = []
        this.providersErrors = []

        let payload = {
          providers: [{'apidae_id' : this.apidaeId}]
        }
        let notFound = []
        let providersRes = []

        this.$http.post('auth/providers/check-apidae-providers', payload).then(res => {
          notFound = res.body.data.providersNotFound
          providersRes = res.body.data.providersFound

          providersRes.forEach(provider => {
            this.checkedProviders.push(provider)
          })
          notFound.forEach(provider => {
            provider.error = {
              code: 1,
              label: 'Fiche inexistante'
            }

            this.notFoundProviders.push(provider)
            this.providersErrors.push(provider)
          })

          this.providersToImport = this.checkedProviders.all()

          this.currentStep += 1
        })
      }
    },
    computed: {
        ...mapState('account', ['tags', 'account'])
    },
    created() {
        this.$global.$on('close-update-modal', () => {
            this.editContactModal = false
        })

        this.$global.$on('update-email', (email) => {
            this.providersToImport.forEach(provider => {
                if (provider === this.selectedProvider) {
                    provider.email = email
                }
            })
        })
    },
    beforeDestroy() {
        this.$global.$off('update-email')
        this.$global.$off('close-update-modal')
    }
}
</script>
